import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { MdAdd, MdEdit, MdDelete, MdPerson } from 'react-icons/md';
import toast from 'react-hot-toast';
import axios from '../lib/axios';
import { endpoints } from '../constants/endpoints';
import { UserRole } from '../constants/roles';
import { useUserStore } from '../store/userStore';
import ConfirmModal from '../components/ConfirmModal';
import AddUserModal from '../components/AddUserModal';
import EditUserModal from '../components/EditUserModal';
import type { User } from '../types/auth';

export default function Users() {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<User | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const currentUser = useUserStore((state) => state.user);
  const queryClient = useQueryClient();

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response: User[] = await axios.get(endpoints.users.list);
      return response;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (userId: number) => {
      return axios.delete(endpoints.users.delete(userId));
    },
    onSuccess: () => {
      toast.success('User deleted');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeleteTarget(null);
    },
    onError: () => {
      setDeleteTarget(null);
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async (user: User) => {
      const url = user.is_active
        ? endpoints.users.deactivate(user.id)
        : endpoints.users.activate(user.id);
      return axios.post(url);
    },
    onSuccess: (_data, user) => {
      toast.success(user.is_active ? 'User deactivated' : 'User activated');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const handleDelete = () => {
    if (!deleteTarget || deleteMutation.isPending) return;
    deleteMutation.mutate(deleteTarget.id);
  };

  const isSuperAdminRow = (user: User) => user.role === UserRole.SUPERADMIN;
  const isCurrentUser = (user: User) => user.id === currentUser?.id;

  if (isLoading) {
    return (
      <div className="flex h-screen flex-col">
        <div className="border-b border-gray-200 bg-white px-6 py-3">
          <h1 className="text-lg font-semibold text-gray-900">Users</h1>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col">
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Users</h1>
          <p className="text-sm text-gray-500">
            Manage dashboard users and their access
          </p>
        </div>
        <button
          onClick={() => setIsAddOpen(true)}
          className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          <MdAdd size={18} />
          Add User
        </button>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {users.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 py-8 text-center">
            <MdPerson size={32} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm text-gray-500">No users found</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                    Name
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                    Email
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                    Role
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                    Created
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {user.first_name} {user.last_name}
                      {isCurrentUser(user) && (
                        <span className="ml-2 text-xs text-gray-400">
                          (you)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {user.email}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          isSuperAdminRow(user)
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {isSuperAdminRow(user) ? 'Super Admin' : 'Admin'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleActiveMutation.mutate(user)}
                          disabled={
                            isSuperAdminRow(user) ||
                            isCurrentUser(user) ||
                            toggleActiveMutation.isPending
                          }
                          className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${
                            user.is_active ? 'bg-green-500' : 'bg-gray-300'
                          } ${
                            isSuperAdminRow(user) || isCurrentUser(user)
                              ? 'cursor-default opacity-50'
                              : 'cursor-pointer'
                          }`}
                          title={
                            isSuperAdminRow(user) || isCurrentUser(user)
                              ? undefined
                              : user.is_active
                                ? 'Deactivate user'
                                : 'Activate user'
                          }
                        >
                          <span
                            className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                              user.is_active
                                ? 'translate-x-4.5'
                                : 'translate-x-0.5'
                            } mt-0.5`}
                          />
                        </button>
                        <span
                          className={`text-xs font-medium ${
                            user.is_active ? 'text-green-700' : 'text-gray-500'
                          }`}
                        >
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {format(new Date(user.created_at), 'd MMM yyyy')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {(!isSuperAdminRow(user) || isCurrentUser(user)) && (
                          <button
                            onClick={() => setEditTarget(user)}
                            className="rounded p-1 text-gray-400 hover:bg-blue-50 hover:text-blue-500"
                            title="Edit user"
                          >
                            <MdEdit size={18} />
                          </button>
                        )}
                        {!isSuperAdminRow(user) && (
                          <button
                            onClick={() => setDeleteTarget(user)}
                            className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
                            title="Delete user"
                          >
                            <MdDelete size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AddUserModal isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} />

      <EditUserModal user={editTarget} onClose={() => setEditTarget(null)} />

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Delete User"
        message={`Are you sure you want to delete ${deleteTarget?.first_name} ${deleteTarget?.last_name}? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        destructive
      />
    </div>
  );
}
